import { Dialog, DialogContent } from "@mui/material";
import React, { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import AddEmployeeFromDatabase from "./AddFromDatabaseFields/AddEmployeeFromDatabase";
import AddLegalEntityFromDatabaseField from "./AddFromDatabaseFields/AddLegalEntityFromDatabaseField";
import { AddFromDatabaseState as State, AddFromDatabaseProps as Props, IntermediatePlaceholder } from "./interfaces";
import { AddFromDatabaseOpener } from "./styles";
import { CompanyLegalEntityDTO } from "../../../../models/company-accounts/company-legal-entities";
import { EmployeeDTO } from "../../../../models/employees";
import { DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES } from "../../../../utils/constants/document-template-placeholder-field-types";
import { intlFormatDateShort } from "../../../../utils/time-utils";
import ConfirmationChangesDialog from "../../../ConfirmationChangesDialog/ConfirmationChangesDialog";
import DialogActions from "../../../DialogActions/DialogActions";
import DialogTitle from "../../../DialogTitle/DialogTitle";

const AddFromDatabase = ({ placeholder, handleUpdatePlaceholders, dependentPlaceholders }: Props) => {
  const { t } = useTranslation();
  const tRP = "components.document_templates.add_from_database";

  const [state, setState] = useState<State>({
    open: false,
    error: "",
    selectedLegalEntity: null,
    selectedLegalEntityProperty: null,
    selectedLegalEntityPropertyKey: undefined,
    selectedEmployee: null,
    selectedEmployeeProperty: null,
    selectedEmployeePropertyKey: undefined,
    CCDOpen: false,
  });

  //#region get subproperty value
  const getLEPropertyValue = useCallback(
    (property: keyof CompanyLegalEntityDTO, propertyKey?: keyof CompanyLegalEntityDTO[keyof CompanyLegalEntityDTO]) => {
      if (property) {
        return !!state.selectedLegalEntity && !!state.selectedLegalEntity[property]
          ? typeof state.selectedLegalEntity[property] === "string" ||
            typeof state.selectedLegalEntity?.[property] === "number"
            ? state.selectedLegalEntity[property]
            : typeof state.selectedLegalEntity?.[property] === "object" &&
              !!propertyKey &&
              propertyKey in state.selectedLegalEntity[property]
            ? (state.selectedLegalEntity[property] as any)[propertyKey]
            : ""
          : "";
      } else {
        return "";
      }
    },
    [state.selectedLegalEntity]
  );
  //#endregion

  //#region get employee subproperty value
  const getEEPropertyValue = useCallback(
    (property?: keyof EmployeeDTO, propertyKey?: keyof EmployeeDTO[keyof EmployeeDTO]) => {
      if (property) {
        return !!state.selectedEmployee && !!state.selectedEmployee[property]
          ? typeof state.selectedEmployee[property] === "string" && !isNaN(Date.parse(state.selectedEmployee[property]))
            ? intlFormatDateShort(new Date(state.selectedEmployee[property]))
            : typeof state.selectedEmployee[property] === "string" ||
              typeof state.selectedEmployee?.[property] === "number"
            ? state.selectedEmployee[property]
            : typeof state.selectedEmployee?.[property] === "object" &&
              !!propertyKey &&
              propertyKey in state.selectedEmployee[property]
            ? (state.selectedEmployee[property] as any)[propertyKey]
            : ""
          : "";
      } else {
        return "";
      }
    },
    [state.selectedEmployee]
  );
  //#endregion

  //#region prehandle update placeholders
  const preHandleUpdatePlaceholders = useCallback(() => {
    const updatedPlaceholder = {
      ...placeholder,
      prefilledPlaceholder: {
        ...placeholder.prefilledPlaceholder,
        value: state.selectedLegalEntityProperty
          ? getLEPropertyValue(state.selectedLegalEntityProperty)
          : state.selectedEmployeeProperty
          ? getEEPropertyValue(state.selectedEmployeeProperty, state.selectedEmployeePropertyKey)
          : "",
      },
    } as IntermediatePlaceholder;

    const updatedDependentPlaceholders: IntermediatePlaceholder[] = dependentPlaceholders.map(
      (dependentPlaceholder) => ({
        ...dependentPlaceholder,
        prefilledPlaceholder: {
          ...dependentPlaceholder.prefilledPlaceholder,
          value:
            placeholder.originalPlaceholder.fieldType === DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.LegalEntity.id &&
            dependentPlaceholder.originalPlaceholder.subfieldType
              ? (state.selectedLegalEntity?.[
                  dependentPlaceholder.originalPlaceholder.subfieldType as keyof CompanyLegalEntityDTO
                ] as string)
              : placeholder.originalPlaceholder.fieldType === DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.Employee.id &&
                dependentPlaceholder.originalPlaceholder.subfieldType
              ? (state.selectedEmployee?.[
                  dependentPlaceholder.originalPlaceholder.subfieldType as keyof EmployeeDTO
                ] as string)
              : "",
        },
      })
    );

    handleUpdatePlaceholders([updatedPlaceholder, ...updatedDependentPlaceholders]);

    setState((state) => ({
      ...state,
      open: false,
    }));
  }, [
    state.selectedEmployee,
    state.selectedLegalEntity,
    dependentPlaceholders,
    handleUpdatePlaceholders,
    placeholder,
    getLEPropertyValue,
    state.selectedLegalEntityProperty,
    state.selectedEmployeePropertyKey,
  ]);

  const renderField = useCallback(() => {
    switch (placeholder.originalPlaceholder.fieldType) {
      case DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.LegalEntity.id: {
        return (
          <>
            <AddLegalEntityFromDatabaseField
              selectedItem={state.selectedLegalEntity}
              selectedItemProperty={state.selectedLegalEntityProperty}
              updateSelectedItem={(field: CompanyLegalEntityDTO) =>
                setState((state) => ({
                  ...state,
                  selectedLegalEntity: field,
                }))
              }
              updateSelectedItemProperty={(
                property: keyof CompanyLegalEntityDTO,
                propertyKey?: keyof CompanyLegalEntityDTO[keyof CompanyLegalEntityDTO]
              ) =>
                setState((state) => ({
                  ...state,
                  selectedLegalEntityProperty: property,
                  selectedLegalEntityPropertyKey: propertyKey,
                }))
              }
              getLEPropertyValue={getLEPropertyValue}
            />
          </>
        );
      }
      case DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.Employee.id: {
        return (
          <>
            <AddEmployeeFromDatabase
              selectedItem={state.selectedEmployee}
              selectedItemProperty={state.selectedEmployeeProperty}
              updateSelectedItem={(item: EmployeeDTO) =>
                setState((state) => ({
                  ...state,
                  selectedEmployee: item,
                }))
              }
              updateSelectedItemProperty={(
                property: keyof EmployeeDTO,
                propertyKey?: keyof EmployeeDTO[keyof EmployeeDTO]
              ) =>
                setState((state) => ({
                  ...state,
                  selectedEmployeeProperty: property,
                  selectedEmployeePropertyKey: propertyKey,
                }))
              }
              getEEPropertyValue={getEEPropertyValue}
              selectedEmployeePropertyKey={state.selectedEmployeePropertyKey}
            />
          </>
        );
      }
    }
  }, [
    placeholder,
    state.selectedEmployee,
    state.selectedLegalEntity,
    state.selectedEmployeeProperty,
    state.selectedLegalEntityProperty,
    state.selectedEmployeePropertyKey,
    state.selectedLegalEntityPropertyKey,
    getEEPropertyValue,
    getLEPropertyValue,
  ]);

  return (
    <>
      <AddFromDatabaseOpener
        onClick={() => setState((state) => ({ ...state, open: true }))}
        style={{ fontSize: "12px", width: "120px" }}
        variant="contained"
      >
        {`${t(`${tRP}.button_title`)}`}
      </AddFromDatabaseOpener>
      <Dialog
        open={state.open}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          title={t(`${tRP}.title`, {
            entity:
              placeholder.originalPlaceholder.fieldType === DOCUMENT_TEMPLATE_PLACEHOLDER_FIELD_TYPES.LegalEntity.id
                ? t("common_words.legal_entity")
                : t("common_words.employee"),
            placeholder: placeholder.originalPlaceholder.name,
          })}
          handleClose={() => setState((state) => ({ ...state, CCDOpen: true }))}
        />
        <DialogContent>{renderField()}</DialogContent>
        <DialogActions
          handleCancel={() => setState((state) => ({ ...state, CCDOpen: true }))}
          handleSave={preHandleUpdatePlaceholders}
          customSaveName={`${t(`buttons.confirm`)}`}
        />
      </Dialog>
      <ConfirmationChangesDialog
        open={state.CCDOpen}
        handleCancel={() => setState((state) => ({ ...state, CCDOpen: false }))}
        handleDiscard={() =>
          setState((state) => ({
            ...state,
            open: false,
            selectedEmployee: null,
            selectedLegalEntity: null,
            CCDOpen: false,
          }))
        }
      />
    </>
  );
};

export default AddFromDatabase;
